import React, { useState, useRef, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import { format, parse } from 'date-fns';
import 'react-day-picker/dist/style.css';

const DatePicker = ({ 
  value, 
  onChange, 
  placeholder = "Select date", 
  className = '',
  required = false,
  placement = 'left',
  yearRange = null // { start: year, end: year } or null for default ±50 years
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(value ? parseDate(value) : undefined);
  const [currentMonth, setCurrentMonth] = useState(selectedDate || new Date());
  const datePickerRef = useRef(null);
  
  // Update internal state when value prop changes
  useEffect(() => {
    setSelectedDate(value ? parseDate(value) : undefined);
  }, [value]);
  
  // Format date as dd/mm/yyyy
  const formatDate = (date) => {
    if (!date) return '';
    return format(date, 'dd/MM/yyyy');
  };
  
  // Parse date from dd/mm/yyyy string
  function parseDate(dateString) {
    if (!dateString) return undefined;
    
    // Handle Date objects
    if (dateString instanceof Date) {
      return dateString;
    }
    
    // Handle dd/mm/yyyy format
    if (typeof dateString === 'string' && dateString.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
      const parsed = parse(dateString, 'dd/MM/yyyy', new Date());
      return isNaN(parsed.getTime()) ? undefined : parsed;
    }
    
    // Handle dd/mm/yy format (for backward compatibility)
    if (typeof dateString === 'string' && dateString.match(/^\d{2}\/\d{2}\/\d{2}$/)) {
      const parsed = parse(dateString, 'dd/MM/yy', new Date());
      return isNaN(parsed.getTime()) ? undefined : parsed;
    }
    
    // Handle dd-mm-yyyy format (for backward compatibility)
    if (typeof dateString === 'string' && dateString.match(/^\d{2}-\d{2}-\d{4}$/)) {
      const parsed = parse(dateString, 'dd-MM-yyyy', new Date());
      return isNaN(parsed.getTime()) ? undefined : parsed;
    }
    
    // Try standard date parsing as fallback
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? undefined : date;
  }
  
  // Close the picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Handle date selection
  const handleDaySelect = (date) => {
    setSelectedDate(date);
    setIsOpen(false);
    
    if (onChange) {
      onChange(date ? formatDate(date) : '');
    }
  };
  
  // Toggle the date picker
  const toggleDatePicker = () => {
    setIsOpen(!isOpen);
  };

  // Handle month change
  const handleMonthChange = (e) => {
    const month = parseInt(e.target.value);
    const newDate = new Date(currentMonth.getFullYear(), month);
    setCurrentMonth(newDate);
  };

  // Handle year change
  const handleYearChange = (e) => {
    const year = parseInt(e.target.value);
    const newDate = new Date(year, currentMonth.getMonth());
    setCurrentMonth(newDate);
  };

  // Generate year options (current year ± 50 years or custom range)
  const currentYear = new Date().getFullYear();
  const startYear = yearRange ? yearRange.start : currentYear - 50;
  const endYear = yearRange ? yearRange.end : currentYear + 50;
  const years = Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i);
  
  // Month names
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <div className="relative" ref={datePickerRef}>
      {/* Custom date input */}
      <div
        onClick={toggleDatePicker}
        className={`w-full px-3 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none cursor-pointer flex items-center shadow-sm hover:border-slate-300 transition-colors ${className}`}
      >
        {selectedDate ? (
          <span className="text-slate-700">{formatDate(selectedDate)}</span>
        ) : (
          <span className="text-slate-400">{placeholder}</span>
        )}
        
        {/* Calendar icon */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
          </svg>
        </div>
      </div>
      
      {/* Hidden input for form submission if needed */}
      {required && (
        <input 
          type="hidden" 
          value={selectedDate ? formatDate(selectedDate) : ''} 
          required={required}
        />
      )}
      
      {/* Date picker dropdown */}
      {isOpen && (
        <div className={`absolute z-50 mt-1 bg-white shadow-lg rounded-md border border-gray-200 overflow-visible ${placement === 'right' ? 'right-0' : 'left-0'}`} style={{ zIndex: 1050 }}>
          {/* Year and Month selectors */}
          <div className="flex gap-2 p-3 border-b border-gray-200 bg-gray-50">
            <select
              value={currentMonth.getMonth()}
              onChange={handleMonthChange}
              className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              {months.map((month, index) => (
                <option key={index} value={index}>
                  {month}
                </option>
              ))}
            </select>
            <select
              value={currentMonth.getFullYear()}
              onChange={handleYearChange}
              className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          
          <DayPicker
            mode="single"
            selected={selectedDate}
            onSelect={handleDaySelect}
            month={currentMonth}
            onMonthChange={setCurrentMonth}
            showOutsideDays
            className="p-2"
          />
        </div>
      )}
    </div>
  );
};

export default DatePicker;
